import { motion } from 'framer-motion';
import { 
  FaCheck, FaBuilding, FaParking, FaThermometerHalf, 
  FaRulerCombined, FaCalendarAlt, FaMapMarkerAlt,
  FaDoorOpen, FaBath, FaLayerGroup
} from 'react-icons/fa';
import Button from './components/Button';
import Card from './components/Card';
import StatCard from './components/StatCard';
import ContactForm from './components/ContactForm';
import ImageGallery from './components/ImageGallery';
import {
  CONTACT_EMAIL,
  PROPERTY_ADDRESS,
  ORIGINAL_PRICE,
  CURRENT_PRICE,
  PRICE_REDUCTION,
  PRICE_PER_SF,
  BUILDING_SIZE,
  PARKING_SPACES,
  LOT_SIZE,
  YEAR_BUILT,
  BUILDING_CLASS,
  LOOPNET_ID
} from './config';

function App() {
  // Scroll to contact section
  const scrollToContact = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  // Gallery images with alt text
  const galleryImages = [
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Building-Photo-2-LargeHighDefinition.jpg',
      alt: 'Two-story professional office building at 6028 Sheridan Drive with brick facade and ample parking'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Building-Photo-3-LargeHighDefinition.jpg',
      alt: 'Side view of 6028 Sheridan Drive showing extensive parking lot and professional landscaping'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Building-Photo-1-Large.jpg',
      alt: 'Front entrance of commercial office building in Williamsville, NY'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-6-LargeHighDefinition.jpg',
      alt: 'Spacious conference room with large table, professional chairs, and natural lighting'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-5-LargeHighDefinition.jpg',
      alt: 'Modern break room with seating areas and professional decor'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-9-LargeHighDefinition.jpg',
      alt: 'Executive office featuring wood desk and professional furnishings'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-4-LargeHighDefinition.jpg',
      alt: 'Private office space with professional setup'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-12-LargeHighDefinition.jpg',
      alt: 'Open bullpen workspace area with modern cubicles'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-13-LargeHighDefinition.jpg',
      alt: 'Professional office workspace with kitchen facilities'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-14-LargeHighDefinition.jpg',
      alt: 'Cubicle workspace area with modern design'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-16-LargeHighDefinition.jpg',
      alt: 'Modern bathroom with contemporary white vanity and fixtures'
    },
    {
      src: '/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-18-LargeHighDefinition.jpg',
      alt: 'Multiple-zone HVAC system showing professional maintenance and quality'
    }
  ];

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="font-body">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Parallax */}
        <motion.div
          className="absolute inset-0 z-0"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('/images/6028-Sheridan-Dr-Williamsville-NY-Building-Photo-2-LargeHighDefinition.jpg')`
            }}
          />
          <div className="absolute inset-0 bg-navy bg-opacity-40" />
        </motion.div>

        {/* Hero Content */}
        <div className="relative z-10 container-custom text-center text-white">
          <motion.div
            initial="initial"
            animate="whileInView"
            variants={staggerContainer}
          >
            <motion.p
              className="text-sm md:text-base uppercase tracking-wider mb-4 opacity-90"
              variants={fadeInUp}
            >
              Class B Office Building | {BUILDING_SIZE.toLocaleString()} SF | Williamsville, NY
            </motion.p>
            
            <motion.h1
              className="text-5xl md:text-7xl font-display font-bold mb-6"
              variants={fadeInUp}
            >
              Premier Professional<br />Office Building
            </motion.h1>
            
            <motion.p
              className="text-xl md:text-2xl mb-8"
              variants={fadeInUp}
            >
              {PROPERTY_ADDRESS}
            </motion.p>
            
            <motion.div
              className="text-6xl font-display font-bold mb-4"
              variants={fadeInUp}
            >
              ${CURRENT_PRICE.toLocaleString()}
            </motion.div>
            
            <motion.p
              className="text-lg md:text-xl mb-6 opacity-90"
              variants={fadeInUp}
            >
              Just ${PRICE_PER_SF} per square foot
            </motion.p>
            
            <motion.div
              className="inline-block bg-brick text-white px-8 py-4 rounded-lg text-lg md:text-xl font-bold mb-8"
              variants={fadeInUp}
            >
              PRICE REDUCED - Save ${PRICE_REDUCTION.toLocaleString()}
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <Button variant="lime" onClick={scrollToContact}>
                Schedule a Showing
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* Quick Stats Bar */}
      <section className="bg-navy py-12">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <StatCard number={BUILDING_SIZE} label="Square Feet" delay={0} suffix=" SF" />
            <StatCard number={PARKING_SPACES} label="Parking Spaces" delay={0.2} />
            <StatCard number={2} label="Levels" delay={0.4} />
            <StatCard number="77.59" label="Price Per SF" delay={0.6} prefix="$" />
          </div>
        </div>
      </section>

      {/* Price Highlight Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-navy transform skew-x-12 translate-x-1/2 opacity-5" />
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-brick text-5xl md:text-6xl font-display font-bold mb-6">
              ${PRICE_REDUCTION.toLocaleString()} PRICE REDUCTION
            </div>
            <div className="text-3xl md:text-4xl text-navy mb-4">
              Originally <span className="line-through text-brick decoration-brick decoration-2">${ORIGINAL_PRICE.toLocaleString()}</span> → NOW <span className="font-bold">${CURRENT_PRICE.toLocaleString()}</span>
            </div>
            <p className="text-xl text-gray-600">
              Just ${PRICE_PER_SF} per square foot - Exceptional value for a Class B office building
            </p>
          </motion.div>
        </div>
      </section>

      {/* Property Highlights */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.h2
            className="text-4xl md:text-5xl font-display font-bold text-navy text-center mb-16"
            {...fadeInUp}
          >
            Property Highlights
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Features List */}
            <motion.div
              className="space-y-6"
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {[
                `${BUILDING_SIZE.toLocaleString()} SF of professional space in excellent condition`,
                `${PARKING_SPACES} parking spaces (3.45 per 1,000 SF)`,
                'Lower level: Private offices and bullpen workspace area',
                'Upper level: Upscale private offices, modern kitchen, conference room',
                '3 full bathrooms and ample storage space',
                'Multiple zone heat/AC for efficiency and comfort',
                'Premier visibility - just west of Transit Road',
                `Built ${YEAR_BUILT}, Class ${BUILDING_CLASS} office building`
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4"
                  variants={fadeInUp}
                >
                  <FaCheck className="text-lime text-2xl flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{feature}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Feature Image */}
            <motion.div
              className="relative rounded-lg overflow-hidden shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="/images/6028-Sheridan-Dr-Williamsville-NY-Interior-Photo-6-LargeHighDefinition.jpg"
                alt="Professional conference room with modern amenities"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section id="gallery" className="section-padding bg-white">
        <div className="container-custom">
          <motion.h2
            className="text-4xl md:text-5xl font-display font-bold text-navy text-center mb-16"
            {...fadeInUp}
          >
            Photo Gallery
          </motion.h2>
          
          <ImageGallery images={galleryImages} />
        </div>
      </section>

      {/* Location Advantage */}
      <section className="section-padding bg-navy text-white">
        <div className="container-custom">
          <motion.h2
            className="text-4xl md:text-5xl font-display font-bold text-center mb-6"
            {...fadeInUp}
          >
            Prime Williamsville Location
          </motion.h2>
          
          <motion.p
            className="text-xl text-white/90 text-center max-w-4xl mx-auto mb-16"
            {...fadeInUp}
          >
            Located in the heart of historic Williamsville, this property offers the perfect blend of small-town charm 
            and big-city accessibility. This walkable village features tree-lined streets, locally-owned shops, craft 
            breweries, and award-winning restaurants—all while providing premier visibility and easy access to Buffalo 
            and Niagara Falls.
          </motion.p>
          
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Map Placeholder */}
            <motion.div
              className="bg-white rounded-lg overflow-hidden shadow-xl"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <iframe
                title="Property Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2916.8!2d-78.72!3d42.98!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d3733f0!2s6028+Sheridan+Dr%2C+Williamsville%2C+NY+14221!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                className="w-full"
              />
              <div className="p-6 bg-gray-50">
                <a
                  href="https://maps.google.com/?q=6028+Sheridan+Drive+Williamsville+NY+14221"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lime hover:text-[#4A7625] font-medium transition-colors inline-flex items-center gap-2"
                >
                  <FaMapMarkerAlt />
                  Open in Google Maps
                </a>
              </div>
            </motion.div>

            {/* Location Benefits */}
            <motion.div
              className="space-y-6"
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp}>
                <h3 className="text-2xl font-display font-bold text-lime mb-4">Why Williamsville?</h3>
              </motion.div>
              
              {[
                'Historic Village Center - Walkable Main Street with 19th century charm and modern amenities',
                'Just West of Transit Road - Premier visibility in the Williamsville/Amherst corridor',
                '8 Minutes to Buffalo Niagara International Airport (3.2 miles)',
                'Thriving Local Scene - Craft breweries, farm-to-table restaurants, artisan shops, and farmers market',
                'Quality of Life - Scenic parks, creekside trails, excellent schools, and safe neighborhoods',
                'Easy Commutes - I-290 access, 15 minutes to Downtown Buffalo via light rail',
                'Business-Friendly - Strong demographics, educated workforce, and growing economy'
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4"
                  variants={fadeInUp}
                >
                  <FaCheck className="text-lime text-2xl flex-shrink-0 mt-1" />
                  <p className="text-lg leading-relaxed">{benefit}</p>
                </motion.div>
              ))}
              
              <motion.div variants={fadeInUp} className="pt-6 border-t border-white/20">
                <p className="text-white/80 mb-3 italic">
                  "A haven to explore... locals call their hidden paradise" - Visit Buffalo Niagara
                </p>
                <a
                  href="https://visitbuffalo.com/neighborhood/williamsville/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lime hover:text-[#4A7625] font-medium transition-colors inline-flex items-center gap-2"
                >
                  Discover Williamsville →
                </a>
              </motion.div>
              
              <motion.div variants={fadeInUp} className="pt-6 border-t border-white/20">
                <div className="text-center">
                  <p className="text-white/90 mb-2 text-lg">
                    🏈 Williamsville is Buffalo - Bills Country! 🏈
                  </p>
                  <p className="text-[#00338D] bg-[#C60C30] font-bold text-2xl py-3 px-6 rounded-lg inline-block">
                    GO BILLS!
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Property Details Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.h2
            className="text-4xl md:text-5xl font-display font-bold text-navy text-center mb-16"
            {...fadeInUp}
          >
            Property Specifications
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card
              icon={FaBuilding}
              title="Building Size"
              description={`${BUILDING_SIZE.toLocaleString()} SF total (4,350 SF per floor)`}
            />
            <Card
              icon={FaRulerCombined}
              title="Lot Size"
              description={`${LOT_SIZE} (177' × 150')`}
            />
            <Card
              icon={FaCalendarAlt}
              title="Year Built"
              description={`${YEAR_BUILT} - Excellent condition`}
            />
            <Card
              icon={FaParking}
              title="Parking"
              description={`${PARKING_SPACES} spaces (3.45 per 1,000 SF)`}
            />
            <Card
              icon={FaThermometerHalf}
              title="HVAC"
              description="Multiple zone heat/AC for maximum efficiency"
            />
            <Card
              icon={FaLayerGroup}
              title="Building Class"
              description={`Class ${BUILDING_CLASS} Office - 2 stories`}
            />
            <Card
              icon={FaDoorOpen}
              title="Lower Level"
              description="Private offices and open bullpen workspace"
            />
            <Card
              icon={FaDoorOpen}
              title="Upper Level"
              description="Upscale offices, modern kitchen, conference room"
            />
            <Card
              icon={FaBath}
              title="Facilities"
              description="3 full bathrooms, ample storage, modern kitchen"
            />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-navy">
        <div className="container-custom">
          <motion.div
            className="text-center mb-12"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
              Schedule a Showing
            </h2>
            <p className="text-xl text-white/90 mb-2">
              Contact us today to schedule a private tour
            </p>
            <p className="text-lg text-white/80">
              Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-lime hover:text-[#4A7625] transition-colors">{CONTACT_EMAIL}</a>
            </p>
            <p className="text-sm text-white/60 mt-4">
              Serious inquiries only - Owner direct
            </p>
          </motion.div>
          
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A3544] text-brand-grey py-12">
        <div className="container-custom">
          <div className="text-center space-y-4">
            <p className="text-lg font-medium text-white">
              {PROPERTY_ADDRESS}
            </p>
            <p className="text-sm">
              {BUILDING_SIZE.toLocaleString()} SF Class {BUILDING_CLASS} Office Building | Built {YEAR_BUILT} | {PARKING_SPACES} Parking Spaces
            </p>
            {/* Drone video link - add URL when available */}
            <div className="border-t border-gray-700 pt-6 mt-6">
              <p className="text-sm">
                © 2026 - Owner Direct Sale
              </p>
              <p className="text-xs mt-2">
                LoopNet ID: {LOOPNET_ID} | Date Listed: April 4, 2024
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
